import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Badge, Button, Card, Col, Form, ProgressBar, Row, Spinner,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExport, faMoneyBillTransfer, faRotateLeft, faTriangleExclamation, faTruckFast,
} from "@fortawesome/free-solid-svg-icons";
import { faGoogleDrive } from "@fortawesome/free-brands-svg-icons";
import { toast } from "react-toastify";
import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";

const SHEET_GROUP = "tiktok_lising_sheets";
const STORAGE_KEY = "paypal_tools_selected_sheet";
const ACCOUNTS_STORAGE_KEY = "paypal_tools_selected_accounts";

const todayIso = () => new Date().toISOString().slice(0, 10);
const monthsAgoIso = (m) => {
  const d = new Date();
  d.setMonth(d.getMonth() - m);
  return d.toISOString().slice(0, 10);
};

const initialProgress = () => ({
  status: "idle", // idle | running | done | error
  message: "",
  current: 0,
  total: 0,
  appended: 0,
  sheetName: "",
  error: "",
});

const PaypalTools = ({ accountList }) => {
  const [sheetsList, setSheetsList] = useState([]);
  const [selectedSheetId, setSelectedSheetId] = useState(
    () => localStorage.getItem(STORAGE_KEY) || ""
  );
  const [loadingSheets, setLoadingSheets] = useState(true);

  const [accountIds, setAccountIds] = useState(() => {
    try {
      const raw = localStorage.getItem(ACCOUNTS_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  const [startDate, setStartDate] = useState(monthsAgoIso(1));
  const [endDate, setEndDate] = useState(todayIso());

  const [running, setRunning] = useState(""); // 'payments' | 'refunds' | 'tracking' | 'addTracking'
  const [progress, setProgress] = useState({
    payments: initialProgress(),
    refunds: initialProgress(),
    tracking: initialProgress(),
    addTracking: initialProgress(),
  });
  const cancelRef = useRef(false);

  useEffect(() => {
    const fetchSheets = async () => {
      try {
        const response = await Utils._apiRequest({
          path: Enums.endpoints.options.LIST,
          type: "GET",
        });
        const options = response.data || [];
        const list = options.filter((o) => o.group === SHEET_GROUP);
        setSheetsList(list);
        if (selectedSheetId && !list.some((s) => s.key === selectedSheetId)) {
          setSelectedSheetId("");
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (e) {
        console.log("fetch sheets:", e.message);
      } finally {
        setLoadingSheets(false);
      }
    };
    fetchSheets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!accountList?.length) return;
    const valid = accountList.map((a) => a._id);
    const filtered = accountIds.filter((id) => valid.includes(id));
    if (filtered.length !== accountIds.length) {
      setAccountIds(filtered.length ? filtered : valid);
      return;
    }
    if (!accountIds.length) setAccountIds(valid);
  }, [accountList]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist account selection to localStorage on every change.
  useEffect(() => {
    try {
      if (accountIds.length) {
        localStorage.setItem(ACCOUNTS_STORAGE_KEY, JSON.stringify(accountIds));
      } else {
        localStorage.removeItem(ACCOUNTS_STORAGE_KEY);
      }
    } catch { /* ignore quota errors */ }
  }, [accountIds]);

  const accountById = useMemo(() => {
    const map = {};
    (accountList || []).forEach((a) => { map[a._id] = a; });
    return map;
  }, [accountList]);
  const accountLabel = (accId) => {
    const a = accountById[accId];
    return a ? `${a.accountName} (${a.meta?.mode || 'live'})` : accId;
  };
  const toggleAccount = (id) => {
    setAccountIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleSelectAllAccounts = () => {
    if (!accountList?.length) return;
    if (accountIds.length === accountList.length) setAccountIds([]);
    else setAccountIds(accountList.map((a) => a._id));
  };

  const handleSheetChange = (e) => {
    const value = e.target.value;
    setSelectedSheetId(value);
    if (value) localStorage.setItem(STORAGE_KEY, value);
    else localStorage.removeItem(STORAGE_KEY);
  };

  const updateProgress = (kind, patch) => {
    setProgress((prev) => ({ ...prev, [kind]: { ...prev[kind], ...patch } }));
  };

  const validateInputs = () => {
    if (!accountIds.length) { toast.warning("Chọn ít nhất 1 tài khoản PayPal"); return false; }
    if (!selectedSheetId) { toast.warning("Chọn Google Sheet để lưu kết quả"); return false; }
    if (!startDate || !endDate) { toast.warning("Chọn khoảng ngày"); return false; }
    return true;
  };

  /* -------------------- PAYMENTS -------------------- */
  const runPayments = async () => {
    if (!validateInputs()) return;
    const kind = "payments";
    setRunning(kind);
    cancelRef.current = false;
    updateProgress(kind, { ...initialProgress(), status: "running", message: "Khởi tạo…" });

    try {
      let grandAppended = 0;
      for (let ai = 0; ai < accountIds.length; ai++) {
        if (cancelRef.current) break;
        const accId = accountIds[ai];
        const accLabel = accountLabel(accId);
        const accPrefix = accountIds.length > 1 ? `[${ai + 1}/${accountIds.length}] ${accLabel} • ` : "";
        updateProgress(kind, { message: `${accPrefix}Khởi tạo…` });

        // eslint-disable-next-line no-await-in-loop
        const init = await Utils._apiRequest({
          path: Enums.endpoints.paypal.EXPORT_PAYMENTS_INIT,
          type: "POST",
          payload: { accountId: accId, sheetId: selectedSheetId, sheetName: "Payments", startDate, endDate },
        });
        if (!init?.ok) throw new Error(init?.errors || `Init thất bại (${accLabel})`);

        const windows = init.windows || [];
        const seen = Array.isArray(init.existingKeys) ? [...init.existingKeys] : [];
        updateProgress(kind, {
          total: windows.length,
          sheetName: init.sheetName,
          message: `${accPrefix}0/${windows.length} cửa sổ • ${seen.length} dòng đã có`,
        });

        let appended = 0;
        for (let i = 0; i < windows.length; i++) {
          if (cancelRef.current) break;
          const w = windows[i];
          // eslint-disable-next-line no-await-in-loop
          const r = await Utils._apiRequest({
            path: Enums.endpoints.paypal.EXPORT_PAYMENTS_CHUNK,
            type: "POST",
            payload: {
              accountId: accId,
              sheetId: selectedSheetId,
              sheetName: init.sheetName,
              headers: init.headers,
              windowStart: w.start,
              windowEnd: w.end,
              existingKeys: seen,
              transactionType: "T0006",
            },
          });
          if (!r?.ok) throw new Error(r?.errors || `Chunk thất bại (${accLabel})`);
          appended += r.count || 0;
          if (Array.isArray(r.addedKeys)) seen.push(...r.addedKeys);
          updateProgress(kind, {
            current: i + 1,
            appended: grandAppended + appended,
            message: `${accPrefix}${i + 1}/${windows.length} cửa sổ — thêm ${appended} dòng mới`,
          });
        }
        grandAppended += appended;
      }
      updateProgress(kind, { status: "done", message: `Hoàn tất • thêm ${grandAppended} dòng mới (${accountIds.length} TK)` });
      toast.success(`Payments: thêm ${grandAppended} dòng mới từ ${accountIds.length} tài khoản`);
    } catch (err) {
      updateProgress(kind, { status: "error", error: err.message });
      toast.error(err.message);
    } finally {
      setRunning("");
    }
  };

  /* -------------------- DISPUTES -------------------- */
  // Removed: Export Disputes has been moved into PaypalDisputes.jsx (DB-sourced).

  /* -------------------- REFUNDS -------------------- */
  const runRefunds = async () => {
    if (!validateInputs()) return;
    const kind = "refunds";
    setRunning(kind);
    cancelRef.current = false;
    updateProgress(kind, { ...initialProgress(), status: "running", message: "Khởi tạo…" });

    try {
      let grandUpdated = 0;
      let grandUnmatched = 0;
      for (let ai = 0; ai < accountIds.length; ai++) {
        if (cancelRef.current) break;
        const accId = accountIds[ai];
        const accLabel = accountLabel(accId);
        const accPrefix = accountIds.length > 1 ? `[${ai + 1}/${accountIds.length}] ${accLabel} • ` : "";

        // eslint-disable-next-line no-await-in-loop
        const init = await Utils._apiRequest({
          path: Enums.endpoints.paypal.EXPORT_REFUNDS_INIT,
          type: "POST",
          payload: { accountId: accId, sheetId: selectedSheetId, sheetName: "Payments", startDate, endDate },
        });
        if (!init?.ok) throw new Error(init?.errors || `Init thất bại (${accLabel})`);

        const windows = init.windows || [];
        const invoiceRowMap = init.invoiceRowMap || {};
        const refundedColLetter = init.refundedColLetter;
        const invoiceCount = Object.keys(invoiceRowMap).length;
        updateProgress(kind, {
          total: windows.length,
          sheetName: init.sheetName,
          message: `${accPrefix}0/${windows.length} cửa sổ • ${invoiceCount} invoice`,
        });

        let totalUpdated = 0;
        let totalUnmatched = 0;
        for (let i = 0; i < windows.length; i++) {
          if (cancelRef.current) break;
          const w = windows[i];
          // eslint-disable-next-line no-await-in-loop
          const r = await Utils._apiRequest({
            path: Enums.endpoints.paypal.EXPORT_REFUNDS_CHUNK,
            type: "POST",
            payload: {
              accountId: accId,
              sheetId: selectedSheetId,
              sheetName: init.sheetName,
              windowStart: w.start,
              windowEnd: w.end,
              invoiceRowMap,
              refundedColLetter,
              transactionTypes: ["T1107", "T1106"],
            },
          });
          if (!r?.ok) throw new Error(r?.errors || `Chunk thất bại (${accLabel})`);
          totalUpdated += r.updated || 0;
          totalUnmatched += r.unmatched || 0;
          updateProgress(kind, {
            current: i + 1,
            appended: grandUpdated + totalUpdated,
            message: `${accPrefix}${i + 1}/${windows.length} cửa sổ — cập nhật ${totalUpdated} invoice${totalUnmatched ? ` • ${totalUnmatched} không match` : ""}`,
          });
        }
        grandUpdated += totalUpdated;
        grandUnmatched += totalUnmatched;
      }
      updateProgress(kind, {
        status: "done",
        message: `Hoàn tất • cập nhật ${grandUpdated} invoice${grandUnmatched ? ` • ${grandUnmatched} không match` : ""} (${accountIds.length} TK)`,
      });
      toast.success(`Refunds: cập nhật ${grandUpdated} invoice từ ${accountIds.length} tài khoản`);
    } catch (err) {
      updateProgress(kind, { status: "error", error: err.message });
      toast.error(err.message);
    } finally {
      setRunning("");
    }
  };

  /* -------------------- TRACKING (auto, account detected per row) -------------------- */
  const runTracking = async () => {
    if (!selectedSheetId) { toast.warning("Chọn Google Sheet để lưu kết quả"); return; }
    const kind = "tracking";
    setRunning(kind);
    cancelRef.current = false;
    updateProgress(kind, { ...initialProgress(), status: "running", message: "Khởi tạo…" });

    try {
      const init = await Utils._apiRequest({
        path: Enums.endpoints.paypal.EXPORT_TRACKING_AUTO_INIT,
        type: "POST",
        payload: { sheetId: selectedSheetId, sheetName: "Payments" },
      });
      if (!init?.ok) {
        const msg = init?.errors === "NO_ACCOUNT_ID_COLUMN"
          ? (init.message || "Sheet thiếu cột Account ID")
          : (init?.errors || "Init thất bại");
        throw new Error(msg);
      }

      const groups = init.groups || [];
      const stats = init.stats || {};
      const totalItems = groups.reduce((sum, g) => sum + (g.items?.length || 0), 0);
      const BATCH = 20;
      const totalBatches = groups.reduce((sum, g) => sum + Math.ceil((g.items?.length || 0) / BATCH), 0);

      updateProgress(kind, {
        total: totalBatches,
        sheetName: init.sheetName,
        message: `0/${totalBatches} batch • ${totalItems} transaction • ${groups.length} account`,
      });

      if (!totalItems) {
        const reasons = [];
        if (stats.alreadyFilledCount) reasons.push(`${stats.alreadyFilledCount} dòng đã có tracking`);
        if (stats.noAccountIdCount) reasons.push(`${stats.noAccountIdCount} dòng thiếu Account ID`);
        if (stats.unknownAccountCount) reasons.push(`${stats.unknownAccountCount} dòng có Account ID không khớp gateway`);
        const detail = reasons.length ? ` • ${reasons.join(' • ')}` : '';
        updateProgress(kind, {
          status: "done",
          message: `Không có dòng nào cần xử lý${detail}`,
        });
        if (stats.noAccountIdCount) {
          toast.warning(`Tracking: ${stats.noAccountIdCount} dòng thiếu cột Account ID. Hãy chạy Export Payments trước để bổ sung.`);
        } else if (stats.unknownAccountCount) {
          toast.warning(`Tracking: ${stats.unknownAccountCount} dòng có Account ID không khớp với gateway nào.`);
        } else {
          toast.info("Tracking: không có dòng nào cần xử lý");
        }
        return;
      }

      let batchDone = 0;
      let grandUpdated = 0;
      let grandMissing = 0;
      for (let gi = 0; gi < groups.length && !cancelRef.current; gi++) {
        const group = groups[gi];
        const groupBatches = Math.ceil(group.items.length / BATCH);
        const accPrefix = `[${gi + 1}/${groups.length}] ${group.accountName} • `;
        for (let i = 0; i < groupBatches && !cancelRef.current; i++) {
          const batch = group.items.slice(i * BATCH, i * BATCH + BATCH);
          // eslint-disable-next-line no-await-in-loop
          const r = await Utils._apiRequest({
            path: Enums.endpoints.paypal.EXPORT_TRACKING_CHUNK,
            type: "POST",
            payload: {
              accountId: group.accountId,
              sheetId: selectedSheetId,
              sheetName: init.sheetName,
              items: batch,
              trackingNumberColLetter: init.trackingNumberColLetter,
              trackingCarrierColLetter: init.trackingCarrierColLetter,
              otherTrackingCarrierColLetter: init.otherTrackingCarrierColLetter,
              trackingNumberColIndex: init.trackingNumberColIndex,
              trackingCarrierColIndex: init.trackingCarrierColIndex,
              otherTrackingCarrierColIndex: init.otherTrackingCarrierColIndex,
            },
          });
          if (!r?.ok) throw new Error(r?.errors || `Chunk thất bại (${group.accountName})`);
          grandUpdated += r.updated || 0;
          grandMissing += r.missing || 0;
          batchDone += 1;
          updateProgress(kind, {
            current: batchDone,
            appended: grandUpdated,
            message: `${accPrefix}${i + 1}/${groupBatches} batch — cập nhật ${grandUpdated}${grandMissing ? ` • ${grandMissing} không có tracking` : ""}`,
          });
        }
      }

      const warnParts = [];
      if (stats.noAccountIdCount) warnParts.push(`${stats.noAccountIdCount} dòng không có Account ID`);
      if (stats.unknownAccountCount) warnParts.push(`${stats.unknownAccountCount} dòng account không tồn tại`);
      const warnSuffix = warnParts.length ? ` • ${warnParts.join(", ")}` : "";
      updateProgress(kind, {
        status: "done",
        message: `Hoàn tất • cập nhật ${grandUpdated}${grandMissing ? ` • ${grandMissing} không có tracking` : ""}${warnSuffix}`,
      });
      toast.success(`Tracking: cập nhật ${grandUpdated} dòng → ${init.sheetName}`);
    } catch (err) {
      updateProgress(kind, { status: "error", error: err.message });
      toast.error(err.message);
    } finally {
      setRunning("");
    }
  };

  /* -------------------- ADD TRACKING (auto, push to PayPal) -------------------- */
  const runAddTracking = async () => {
    if (!selectedSheetId) { toast.warning("Chọn Google Sheet trước"); return; }
    const kind = "addTracking";
    setRunning(kind);
    cancelRef.current = false;
    updateProgress(kind, { ...initialProgress(), status: "running", message: "Khởi tạo…" });

    try {
      const init = await Utils._apiRequest({
        path: Enums.endpoints.paypal.ADD_TRACKING_AUTO_INIT,
        type: "POST",
        payload: { sheetId: selectedSheetId, sheetName: "Payments" },
      });
      if (!init?.ok) {
        const msg = init?.errors === "NO_ACCOUNT_ID_COLUMN"
          ? (init.message || "Sheet thiếu cột Account ID")
          : (init?.errors || "Init thất bại");
        throw new Error(msg);
      }

      const groups = init.groups || [];
      const stats = init.stats || {};
      const totalItems = groups.reduce((sum, g) => sum + (g.items?.length || 0), 0);
      const BATCH = 20;
      const totalBatches = groups.reduce((sum, g) => sum + Math.ceil((g.items?.length || 0) / BATCH), 0);

      updateProgress(kind, {
        total: totalBatches,
        sheetName: init.sheetName,
        message: `0/${totalBatches} batch • ${totalItems} tracking cần push • ${groups.length} account`,
      });

      if (!totalItems) {
        const reasons = [];
        if (stats.skippedAlreadyColored) reasons.push(`${stats.skippedAlreadyColored} dòng đã sync (ô xanh)`);
        if (stats.skippedIncomplete) reasons.push(`${stats.skippedIncomplete} dòng chưa đủ tracking`);
        if (stats.noAccountIdCount) reasons.push(`${stats.noAccountIdCount} dòng thiếu Account ID`);
        if (stats.unknownAccountCount) reasons.push(`${stats.unknownAccountCount} dòng có Account ID không khớp gateway`);
        const detail = reasons.length ? ` • ${reasons.join(' • ')}` : '';
        updateProgress(kind, { status: "done", message: `Không có tracking mới nào cần push${detail}` });
        if (stats.noAccountIdCount) {
          toast.warning(`Add Tracking: ${stats.noAccountIdCount} dòng thiếu cột Account ID.`);
        } else if (stats.unknownAccountCount) {
          toast.warning(`Add Tracking: ${stats.unknownAccountCount} dòng có Account ID không khớp với gateway nào.`);
        } else {
          toast.info("Add Tracking: không có tracking mới nào cần push");
        }
        return;
      }

      let batchDone = 0;
      let grandAdded = 0;
      let grandFailed = 0;
      for (let gi = 0; gi < groups.length && !cancelRef.current; gi++) {
        const group = groups[gi];
        const groupBatches = Math.ceil(group.items.length / BATCH);
        const accPrefix = `[${gi + 1}/${groups.length}] ${group.accountName} • `;
        for (let i = 0; i < groupBatches && !cancelRef.current; i++) {
          const batch = group.items.slice(i * BATCH, i * BATCH + BATCH);
          // eslint-disable-next-line no-await-in-loop
          const r = await Utils._apiRequest({
            path: Enums.endpoints.paypal.ADD_TRACKING_CHUNK,
            type: "POST",
            payload: {
              accountId: group.accountId,
              sheetId: selectedSheetId,
              sheetName: init.sheetName,
              items: batch,
              gridSheetId: init.gridSheetId,
              trackingNumberColIndex: init.trackingNumberColIndex,
              trackingCarrierColIndex: init.trackingCarrierColIndex,
              otherTrackingCarrierColIndex: init.otherTrackingCarrierColIndex,
            },
          });
          if (!r?.ok) throw new Error(r?.errors || `Chunk thất bại (${group.accountName})`);
          grandAdded += r.added || 0;
          grandFailed += r.failed || 0;
          batchDone += 1;
          updateProgress(kind, {
            current: batchDone,
            appended: grandAdded,
            message: `${accPrefix}${i + 1}/${groupBatches} batch — đã add ${grandAdded}${grandFailed ? ` • ${grandFailed} lỗi` : ""}`,
          });
        }
      }

      const warnParts = [];
      if (stats.noAccountIdCount) warnParts.push(`${stats.noAccountIdCount} dòng không có Account ID`);
      if (stats.unknownAccountCount) warnParts.push(`${stats.unknownAccountCount} dòng account không tồn tại`);
      const warnSuffix = warnParts.length ? ` • ${warnParts.join(", ")}` : "";
      updateProgress(kind, {
        status: "done",
        message: `Hoàn tất • add ${grandAdded}${grandFailed ? ` • ${grandFailed} lỗi` : ""}${warnSuffix}`,
      });
      toast.success(`Add Tracking: ${grandAdded} thành công${grandFailed ? `, ${grandFailed} lỗi` : ""}`);
    } catch (err) {
      updateProgress(kind, { status: "error", error: err.message });
      toast.error(err.message);
    } finally {
      setRunning("");
    }
  };

  const renderProgress = (kind) => {
    const p = progress[kind];
    if (p.status === "idle") return null;

    const variant = p.status === "error" ? "danger" : p.status === "done" ? "success" : "info";
    const pct = p.total > 0
      ? Math.round((p.current / p.total) * 100)
      : (p.status === "running" ? 100 : 0);

    return (
      <div className="mt-2">
        <ProgressBar
          now={pct}
          striped={p.status === "running"}
          animated={p.status === "running"}
          variant={variant}
          label={p.total > 0 ? `${p.current}/${p.total}` : ""}
          style={{ height: 14 }}
        />
        <div className="small mt-1">
          {p.status === "error" ? (
            <Badge bg="danger">Lỗi: {p.error}</Badge>
          ) : (
            <span className={p.status === "done" ? "text-success" : "text-muted"}>
              {p.message}
              {p.sheetName && p.status === "done" && (
                <Badge bg="success" className="ms-2">→ {p.sheetName}</Badge>
              )}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderRunButton = (kind, label, variant, onClick) => {
    const isRunning = running === kind;
    return (
      <Button size="sm" variant={variant} disabled={!!running} onClick={onClick}>
        {isRunning ? (
          <><Spinner size="sm" animation="border" /> Đang chạy…</>
        ) : label}
      </Button>
    );
  };

  return (
    <div className="my-3">
      <Card className="mb-3">
        <Card.Header className="d-flex align-items-center">
          <FontAwesomeIcon icon={faFileExport} className="me-2" />
          <strong>Công cụ Export</strong>
        </Card.Header>
        <Card.Body>
          {accountList?.length === 0 && (
            <Alert variant="warning" className="py-2">
              <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
              Chưa có tài khoản PayPal nào. Hãy thêm tài khoản ở phía trên trước.
            </Alert>
          )}

          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <div className="d-flex justify-content-between align-items-center">
                  <Form.Label className="small mb-1">
                    Tài khoản PayPal{" "}
                    <span className="text-muted">
                      ({accountIds.length}/{accountList?.length || 0})
                    </span>
                  </Form.Label>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 small"
                    onClick={toggleSelectAllAccounts}
                    disabled={!accountList?.length || !!running}
                  >
                    {accountIds.length === (accountList?.length || 0) ? "Bỏ chọn tất cả" : "Chọn tất cả"}
                  </Button>
                </div>
                <div
                  className="border rounded p-2"
                  style={{ maxHeight: 110, overflowY: "auto", background: "#fff" }}
                >
                  {(accountList || []).length === 0 && (
                    <div className="small text-muted">Chưa có tài khoản</div>
                  )}
                  {(accountList || []).map((a) => (
                    <Form.Check
                      key={a._id}
                      type="checkbox"
                      id={`pp-acc-${a._id}`}
                      className="small"
                      checked={accountIds.includes(a._id)}
                      onChange={() => toggleAccount(a._id)}
                      disabled={!!running}
                      label={`${a.accountName} (${a.meta?.mode || "live"})`}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Từ ngày</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={!!running}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small">Đến ngày</Form.Label>
                <Form.Control
                  type="date"
                  size="sm"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={!!running}
                />
              </Form.Group>
            </Col>
            <Col md={2}></Col>

            <Col md={9}>
              <Form.Group>
                <Form.Label className="small">Google Sheet lưu kết quả</Form.Label>
                <Form.Select
                  size="sm"
                  value={selectedSheetId}
                  onChange={handleSheetChange}
                  disabled={loadingSheets || !!running}
                >
                  <option value="">-- Chọn sheet đã lưu --</option>
                  {sheetsList.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.values?.name || s.key}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button
                as={Link}
                to="/settings#google-sheet"
                variant="dark"
                size="sm"
                className="w-100"
              >
                <FontAwesomeIcon icon={faGoogleDrive} className="me-2" />
                Quản lý Sheets
              </Button>
            </Col>
          </Row>

          <hr />

          <Row className="g-3">
            <Col md={6} lg={4}>
              <Card className="h-100">
                <Card.Body>
                  <h6>
                    <FontAwesomeIcon icon={faMoneyBillTransfer} className="me-2 text-primary" />
                    Export Payments
                  </h6>
                  <p className="small text-muted mb-2">
                    Lấy giao dịch nhận tiền (T0006) từ PayPal, ghi vào tab <code>Payments</code>.
                  </p>
                  {renderRunButton("payments", "Export Payments", "primary", runPayments)}
                  {renderProgress("payments")}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="h-100">
                <Card.Body>
                  <h6>
                    <FontAwesomeIcon icon={faRotateLeft} className="me-2 text-danger" />
                    Export Refunds
                  </h6>
                  <p className="small text-muted mb-2">
                    Lấy refund (gồm cả chargeback) và cập nhật vào cột <code>Refunded</code> của sheet <code>Payments</code> theo <code>Invoice</code>.
                  </p>
                  {renderRunButton("refunds", "Export Refunds", "danger", runRefunds)}
                  {renderProgress("refunds")}
                </Card.Body>
              </Card>
            </Col>
            <Col md={12} lg={4}>
              <Card className="h-100">
                <Card.Body>
                  <h6>
                    <FontAwesomeIcon icon={faTruckFast} className="me-2 text-info" />
                    Shipping Tracker
                    <Badge bg="info" className="ms-2">Auto</Badge>
                  </h6>
                  <p className="small text-muted mb-2">
                    <strong>Export</strong>: kéo tracking từ PayPal về sheet <code>Payments</code>.<br />
                    <strong>Add</strong>: đẩy tracking nhập tay từ sheet lên PayPal.
                  </p>
                  <Alert variant="info" className="py-1 px-2 small mb-2">
                    <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />
                    Tự xác định account theo cột <code>Account ID</code>. Dòng thiếu cột này sẽ bị bỏ qua — chạy <em>Export Payments</em> trước để bổ sung.
                  </Alert>
                  <div className="d-flex gap-2 flex-wrap">
                    {renderRunButton("tracking", "Export Tracking", "info", runTracking)}
                    {renderRunButton("addTracking", "Add Tracking", "success", runAddTracking)}
                  </div>
                  {renderProgress("tracking")}
                  {renderProgress("addTracking")}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default PaypalTools;
